import React from 'react';
import PropTypes from 'prop-types';

/**
 * MUI Compoments
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';

const WorkBasicInfoModal = ({ open, handleClose, handleViewReports, planning }) => {
    const { t } = useTranslation();

    const StaffResponsable = () => {
        const responsable = planning.responsable;
        if (responsable) {
            return (
                <>
                    <h3>{t('Responsable')}: {responsable.full_name}</h3>
                </>
            );
        }

        return;
    };

    const ShowTask = () => {
        return (
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    {t('Task')}
                </AccordionSummary>
                <AccordionDetails>
                    {planning.task || t('No task')}
                </AccordionDetails>
            </Accordion>
        );
    };

    const ShowNotes = () => {
        return (
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    {t('Notes')}
                </AccordionSummary>
                <AccordionDetails>
                    {planning.work.notes || t('No notes')}
                </AccordionDetails>
            </Accordion>
        );
    };

    const ShowCategories = () => {
        const disableWorkActionAreas = !(Array.isArray(planning.work.work_action_areas) && planning.work.work_action_areas.length > 0);
        const disableCatalogServices = !(Array.isArray(planning.work.catalog_services) && planning.work.catalog_services.length > 0);

        const serviceNameWithActivities = (service) => {
            if (service.activities && service.activities.length > 0) {
                return service.name + ' (' + service.activities.map(activity => activity.name).join(', ') + ')';
            }

            return service.name;
        }

        return (
            <>
                {/* Work action areas and subareas */}
                <Accordion disabled={disableWorkActionAreas}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        {t('Work action areas')}
                    </AccordionSummary>
                    <AccordionDetails style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        userSelect: 'none'
                    }}>
                        <List>
                            {/* Action Areas list */}
                            {planning.work.work_action_areas && planning.work.work_action_areas.length > 0 ? (
                                planning.work.work_action_areas.map((area, index) => (
                                    <ListItem key={index}>
                                        <ListItemText primary={area.name} secondary={area.description} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary="No work areas available" />
                                </ListItem>
                            )}

                            {/* Action subareas like Rodal, tramo, etc here... */}
                            {planning.work?.work_action_subareas && planning.work.work_action_subareas.length > 0 ? (
                                planning.work.work_action_subareas.map((area, index) => (
                                    <ListItem key={index}>
                                        <ListItemText primary={area.name} secondary={area.description} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary="No work areas available" />
                                </ListItem>
                            )}
                        </List>
                    </AccordionDetails>
                </Accordion>

                {/* Catalog services */}
                <Accordion disabled={disableCatalogServices}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        {t('Catalog services')}
                    </AccordionSummary>
                    <AccordionDetails style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        userSelect: 'none'
                    }}>
                        <List sx={{ padding: 0, textAlign: 'left' }}>
                            {planning.work.catalog_services && planning.work.catalog_services.length > 0 ? (
                                planning.work.catalog_services.map((service, index) => (
                                    <ListItem key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}>
                                        <ListItemText primary={serviceNameWithActivities(service)} secondary={service.description} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary="No catalog services available" />
                                </ListItem>
                            )}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </>
        );
    };

    const ShowVehicles = () => {
        const disableVehicles = !(Array.isArray(planning.vehicles) && planning.vehicles.length > 0);

        return (
            <Accordion disabled={disableVehicles}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    {t('Vehicles')}
                </AccordionSummary>
                <AccordionDetails style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    userSelect: 'none'
                }}>
                    <List>
                        {planning.vehicles && planning.vehicles.length > 0 ? (
                            planning.vehicles.map((vehicle, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={vehicle.code}
                                        secondary={
                                            <React.Fragment>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{ color: 'text.primary', display: 'inline' }}
                                                >
                                                    {vehicle.denomination}
                                                </Typography>
                                                {` - ${vehicle.plate_number} - ${vehicle.has_roof_rack ? t('With roof rack') : t('Without roof rack')}`}
                                            </React.Fragment>
                                        } />
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>
                                <ListItemText primary={t('No vehicles available')} />
                            </ListItem>
                        )}
                    </List>
                </AccordionDetails>
            </Accordion>
        );
    };

    const ShowTrailers = () => {
        const disableTrailers = !(Array.isArray(planning.trailers) && planning.trailers.length > 0);

        return (
            <Accordion disabled={disableTrailers}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    {t('Trailers')}
                </AccordionSummary>
                <AccordionDetails style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    userSelect: 'none'
                }}>
                    <List>
                        {planning.trailers && planning.trailers.length > 0 ? (
                            planning.trailers.map((trailer, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={trailer.code}
                                        secondary={
                                            <React.Fragment>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{ color: 'text.primary', display: 'inline' }}
                                                >
                                                    {trailer.denomination}
                                                </Typography>
                                            </React.Fragment>
                                        } />
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>
                                <ListItemText primary={t('No trailers available')} />
                            </ListItem>
                        )}
                    </List>
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '100vh',
                    overflowY: 'auto'
                }}
            >
                <Typography variant="h6" component="h2">
                    {planning.work.name}
                </Typography>

                <Typography variant='p' component="p">
                    {t('Work kind')} {planning.work.kind_name}
                </Typography>

                <Typography variant='p' component="p">
                    {t('Work type')} {planning.work.work_type_name}
                </Typography>

                <StaffResponsable />

                <Box style={{
                    marginTop: '20px'
                }}>
                    <ShowTask />
                    <ShowNotes />
                    <ShowCategories />
                    <ShowVehicles />
                    {/* <ShowTrailers /> */}
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: '20px',
                    justifyContent: 'space-between',
                    marginTop: 2
                }}>
                    <Button onClick={handleClose} variant="contained" color="primary">
                        {t('Close')}
                    </Button>
                    <Button disabled={!(handleViewReports)} onClick={handleViewReports} variant="contained" color="secondary">
                        {t('View reports')}
                    </Button>
                </Box>
            </Box>
        </Modal >
    );
};

WorkBasicInfoModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    planning: PropTypes.object.isRequired,
};

export default WorkBasicInfoModal;
